
import React, { useState, useEffect } from 'react';
import MasterLicenseLayout from '@/components/masterlicense/MasterLicenseLayout';
import { useMasterLicense } from '@/hooks/useMasterLicense';
import { useResellerPlans } from '@/hooks/license/useResellerPlans';
import { ResellerPlanDetails } from '@/hooks/license/resellerPlans/types';
import { useToast } from '@/hooks/use-toast';
import ResellerPlansContent from '@/components/masterlicense/reseller/ResellerPlansContent';
import { ActivePlan } from '@/hooks/license/useResellerPlanManagement';
import { PhoneOrderDialogProvider } from '@/contexts/PhoneOrderDialogContext';
import PhoneOrderDialog from '@/components/masterlicense/PhoneOrderDialog';
import PlanOrderButton from '@/components/masterlicense/PlanOrderButton';

const MasterLicenseResellerPlans = () => {
  const { toast } = useToast();
  const { isLoading, handleLogout } = useMasterLicense();
  const { resellerPlans, isLoadingPlans, isSavingPlans, saveResellerPlans, updateResellerPlan, addResellerPlan } = useResellerPlans(false);
  
  const [currentPlanIndex, setCurrentPlanIndex] = useState(0);
  const [activePlans, setActivePlans] = useState<ActivePlan[]>([]);
  
  // Create active plans from actual reseller plans data
  useEffect(() => {
    if (resellerPlans.length > 0) {
      const newActivePlans = resellerPlans.map(plan => ({
        id: plan.id,
        name: plan.name,
        status: 'Active',
        admins: plan.maxAdmins,
        users: plan.maxUsers,
        enabled: true
      }));
      setActivePlans(newActivePlans);
    }
  }, [resellerPlans]);
  
  // Current plan is the one at currentPlanIndex or null
  const currentPlan = resellerPlans[currentPlanIndex] || null;
  
  // Current plan for the active plans card
  const currentPlanForCard = currentPlan ? {
    id: currentPlan.id,
    name: currentPlan.name,
    status: 'Active',
    admins: currentPlan.maxAdmins,
    users: currentPlan.maxUsers,
    enabled: true
  } : null;
  
  const handleUpdatePlan = (field: keyof ResellerPlanDetails, value: any) => {
    updateResellerPlan(currentPlanIndex, field, value);
  };
  
  const handleAddFeature = () => {
    const updatedFeatures = [
      ...(currentPlan?.features || []), 
      { name: 'New Feature', included: true }
    ];
    
    updateResellerPlan(currentPlanIndex, 'features', updatedFeatures);
  };
  
  const handleAddPlan = () => {
    addResellerPlan();
    // Set the new plan as the current one (it will be the last one in the array)
    setCurrentPlanIndex(resellerPlans.length);
    
    toast({
      title: "New Plan Added",
      description: "A new reseller plan has been created and is ready to edit",
    });
  };
  
  const handleSavePlan = async (): Promise<boolean> => {
    try {
      const success = await saveResellerPlans();
      
      if (success) {
        toast({
          title: "Plan Saved",
          description: `${currentPlan?.name} plan has been saved successfully`,
        });
        
        // Update active plans after save
        if (currentPlan) {
          setActivePlans(prev => 
            prev.map(plan => 
              plan.id === currentPlan.id 
                ? { 
                    ...plan, 
                    name: currentPlan.name,
                    admins: currentPlan.maxAdmins,
                    users: currentPlan.maxUsers 
                  } 
                : plan
            )
          );
        }
      } else {
        toast({
          title: "Error Saving Plan",
          description: "There was a problem saving the plan",
          variant: "destructive",
        });
      }
      
      return success;
    } catch (error) {
      console.error('Error saving plan:', error);
      toast({
        title: "Error Saving Plan",
        description: "An unexpected error occurred while saving the plan",
        variant: "destructive",
      });
      return false;
    }
  };
  
  const handleTogglePlanEnabled = (planId: string, enabled: boolean) => {
    setActivePlans(prevPlans => 
      prevPlans.map(plan => 
        plan.id === planId ? { ...plan, enabled } : plan
      )
    );
    
    toast({
      title: enabled ? "Plan Enabled" : "Plan Disabled",
      description: `${activePlans.find(p => p.id === planId)?.name} has been ${enabled ? 'enabled' : 'disabled'}`,
    });
  };
  
  if (isLoading || isLoadingPlans) {
    return (
      <MasterLicenseLayout
        isLoading={false}
        onLogout={handleLogout}
        activeTab="reseller-plans"
      >
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-blue"></div>
          <p className="ml-2">Loading...</p>
        </div>
      </MasterLicenseLayout>
    );
  }

  return (
    <PhoneOrderDialogProvider>
      <MasterLicenseLayout
        isLoading={isLoading}
        onLogout={handleLogout}
        activeTab="reseller-plans"
      >
        <div className="flex justify-end mb-4 px-4">
          <PlanOrderButton />
        </div>
        
        {currentPlan && (
          <ResellerPlansContent 
            plans={resellerPlans}
            currentPlan={currentPlan}
            currentPlanIndex={currentPlanIndex}
            activePlans={activePlans}
            currentPlanForCard={currentPlanForCard}
            onSelectPlan={setCurrentPlanIndex}
            onAddPlan={handleAddPlan}
            onUpdatePlan={handleUpdatePlan}
            onAddFeature={handleAddFeature}
            onSavePlan={handleSavePlan}
            onTogglePlanEnabled={handleTogglePlanEnabled}
            isSavingPlan={isSavingPlans}
          />
        )}
        
        <PhoneOrderDialog />
      </MasterLicenseLayout>
    </PhoneOrderDialogProvider>
  );
};

export default MasterLicenseResellerPlans;
